import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import useChatScroll from '../hooks/useChatScroll';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
    Send, MessageCircle, User, Check, CheckCheck, ChevronRight, ArrowLeft,
    CreditCard, Wrench, ClipboardList
} from 'lucide-react';
import {
    AdjuntarImagen, ImagenDelMensaje, PreviaDelAdjunto,
} from '../components/chat/ImagenAdjunta';

// EL CHAT CON TRES ENTRADAS (doc del 21-08, apartados 13 y 20; la tercera, del parte de
// Gonzalo del 4-09, fichas 87, 88 y 118).
//
// Antes esta pantalla era una sola conversación para todo, y eso producía la
// contradicción del catálogo: al plan Calculadora se le decía «Solo incidencias
// técnicas» pero se le escondía el chat entero, y con él la única vía para preguntar
// por su dinero. Ahora se entra eligiendo de qué va la cosa, y cada entrada abre la
// conversación de siempre etiquetada con su canal:
//
//   - «Mi plan»: dudas de la dieta, los macros o el entrenamiento. Es lo que el Gold
//     paga como «Chat de dudas» en el catálogo y hasta el 5-09 no tenía puerta: solo
//     podía preguntar por su dinero o decir que algo no funciona. Con el horario debajo.
//     LA VEN LOS PLANES CON CHAT DE DUDAS (`can('chat_dudas')`, lib/planAccess.js): los
//     que llevan entrenador detrás, salvo que su ficha del catálogo diga que su canal es
//     otra cosa. No es una puerta para todo el mundo: sin Chat no hay ni pantalla.
//   - «Mi suscripción»: cobros, renovación, cambio de plan o baja. Contesta el equipo.
//     La promesa de plazo del plan (`tiempo_respuesta`) SE QUEDA aquí: hay dinero de
//     por medio.
//   - «Algo no funciona»: la app no carga, no guarda... SIN promesa de 24 horas: lo
//     que el doc quiere ahí es un respondedor automático con el equipo detrás, y eso
//     es un agente aparte que no es de hoy.
//
// El canal viaja con cada mensaje al backend («plan» | «suscripcion» | «tecnico») y el
// panel de Mensajes del equipo lo enseña como chip.
//
// EL HORARIO DE «MI PLAN» ES FIJO porque no hay ninguno escrito en el código ni en el
// catálogo (lo único parecido es `tiempo_respuesta`, que es un plazo y se queda en «Mi
// suscripción»). El día que el catálogo lleve horario, se lee de ahí.
const CANAL = {
    plan: {
        etiqueta: 'Mi plan', detalle: 'Dudas de la dieta, los macros o el entrenamiento',
        horario: 'Te contestamos de lunes a viernes', icono: ClipboardList, cap: 'chat_dudas',
    },
    suscripcion: { etiqueta: 'Mi suscripción', detalle: 'Cobros, renovación, cambio de plan o baja', icono: CreditCard },
    tecnico: { etiqueta: 'Algo no funciona', detalle: 'La app no carga, no me guarda, no encuentro dónde se hace', icono: Wrench },
};

const MessagesPage = () => {
    const { api, user, profile, habilitaciones, can } = useAuth();
    const [canal, setCanal] = useState(null); // null = pantalla de las entradas
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    // La imagen ya subida y esperando a que se mande el mensaje (null si no hay).
    const [adjunto, setAdjunto] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    const trainerId = profile?.trainer_id || 'support';

    // El Premium habla de lo suyo con su entrenador por WhatsApp. Sale del CATÁLOGO
    // (habilitaciones.acompanamiento), jamás del nombre del plan: un plan legacy con
    // llamadas también lo es.
    //
    // Y CON LLAVE PROPIA (02-10): `whatsapp_directo` del catálogo manda si está escrita. El
    // catálogo del 29-09 quita las llamadas al Premium -- que sigue siendo WhatsApp directo
    // con Jesús -- y se las da al Reto12en12, cuyo WhatsApp es el de José Luis o Monse y no
    // el número único de los ajustes. Sin la llave, lo de siempre: el plan con llamadas.
    const seguimientoPorWhatsapp = habilitaciones?.whatsapp_directo !== undefined
        ? !!habilitaciones.whatsapp_directo
        : habilitaciones?.acompanamiento === 'con_entrenador_y_llamadas';
    // Y AHORA LE LLEVA (ficha 89 de Gonzalo): la tarjeta decía «por WhatsApp» y no tenía
    // botón, porque el número no existía en ningún sitio. Lo pone el equipo desde el panel
    // («Ajustes de la app» · WhatsApp del entrenador) y viaja en el perfil ya limpio, solo
    // dígitos con el prefijo del país.
    const whatsapp = String(profile?.whatsapp_entrenador || '').replace(/\D/g, '');
    // Las puertas que ve ESTE cliente: «Mi plan» solo si su plan lleva chat de dudas.
    const puertas = Object.entries(CANAL).filter(([, c]) => !c.cap || can(c.cap));

    // LAS RESPUESTAS SIN LEER, EN LA PANTALLA DE ENTRADA (26-09, Juan Merino: «veo en el icono
    // del chat que tengo 3 mensajes sin leer, pero no los acabo de ver»). El icono y la campana
    // traen aquí, y aquí solo había «¿Con qué te ayudamos?» y tres «Escribir»: nada decía que
    // le habían contestado ni dónde leerlo. Ahora se dice y se abre la conversación por la
    // entrada que usó la última vez (la conversación es la misma en las tres).
    const [sinLeer, setSinLeer] = useState(0);
    const [ultimaEntrada, setUltimaEntrada] = useState(null);
    useEffect(() => {
        if (canal) return;
        api.get('/messages/unread-count?vista=cliente').then((r) => setSinLeer(r.data?.count || 0)).catch(() => {});
        api.get(`/messages?with_user=${trainerId}`).then((r) => {
            const mio = (r.data || []).find((m) => m.sender_id === user?.id && m.canal && CANAL[m.canal]);
            setUltimaEntrada(mio?.canal || null);
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps -- al volver a la entrada
    }, [canal, trainerId]);
    const entradaParaLeer = (ultimaEntrada && puertas.some(([k]) => k === ultimaEntrada))
        ? ultimaEntrada : (puertas.find(([k]) => k === 'tecnico') || puertas[0])?.[0];

    useEffect(() => {
        // La conversación solo se carga (y solo se marca como leída) cuando se entra
        // por una de las dos puertas: leer por debajo de la pantalla de entrada
        // marcaría como vistos mensajes que nadie vio.
        if (!canal) return;
        fetchMessages();
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- repoll al cambiar trainerId o canal
    }, [trainerId, canal]);

    // BAJA SOLO CUANDO TOCA (27-09): al abrir y con un mensaje nuevo si estaba abajo o es suyo.
    // Antes bajaba en cada refresco de 5 s y no dejaba leer lo de arriba (hooks/useChatScroll).
    useChatScroll(scrollRef, messages, user?.id, `${trainerId}|${canal}`);

    const fetchMessages = async () => {
        try {
            const response = await api.get(`/messages?with_user=${trainerId}`);
            setMessages(response.data.reverse());

            response.data.forEach(async (msg) => {
                if (!msg.read && msg.receiver_id === user.id) {
                    await api.put(`/messages/${msg.id}/read`);
                }
            });
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        // Una imagen sola es un mensaje: si hay adjunto, no hace falta escribir nada.
        if (!newMessage.trim() && !adjunto) return;

        setSending(true);
        try {
            await api.post('/messages', {
                receiver_id: trainerId,
                content: newMessage.trim(),
                // La etiqueta de la puerta por la que entró: es lo que el equipo ve
                // como chip en su bandeja.
                canal,
                adjunto_id: adjunto?.id || null,
            });
            setNewMessage('');
            setAdjunto(null);
            fetchMessages();
            inputRef.current?.focus();
        } catch (error) {
            toast.error('Error al enviar mensaje');
        } finally {
            setSending(false);
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Ayer ' + date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
        }
    };

    // ─── La pantalla de entrada: «¿Con qué te ayudamos?» ───────────────────────
    // Tarjetas como las del Inicio (mockup del apartado 20 del doc).
    if (!canal) {
        return (
            <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-4 animate-fade-in bg-background" data-testid="chat-entradas">
                <div>
                    <h1 className="text-xl font-bold text-foreground uppercase tracking-wider" data-testid="messages-heading">
                        ¿Con qué te ayudamos?
                    </h1>
                    {/* QUIÉN LE CONTESTA, Y SEGÚN LO QUE HAYA COMPRADO (Francisco, 5-09).
                        Son tres casos y no dos, y confundirlos es prometer lo que no se
                        vende:

                        · Su plan lleva entrenador y lo tiene asignado -> se dice su nombre.
                        · Su plan lleva entrenador y no lo tiene asignado -> se dice que le
                          contesta el equipo, SIN negar el entrenador. Antes ponía «todavía
                          no tienes entrenador asignado», y eso choca de frente con lo que su
                          propio plan le promete -- «entrenador detrás de tus números» --:
                          una pantalla le vende un entrenador y la de al lado le dice que no
                          tiene (Jesús, 11-09, de bulto). Son 21 de los 99 activos con un
                          plan que lo incluye; los otros 78 sí lo tienen, y a esos se les
                          dice su nombre. El hueco es de asignación, no de texto, así que el
                          texto no lo tapa ni lo agranda: dice quién contesta hoy.
                        · Su plan NO lleva entrenador -> del entrenador no se le habla. Aquí
                          entra para avisar de un cobro o de que la app no le carga, y eso lo
                          contesta el equipo. Un «todavía no tienes entrenador asignado» le
                          estaría anunciando uno que no ha comprado.

                        El chat CON entrenador sigue dependiendo del plan: la puerta «Mi
                        plan» solo la ve quien lo lleva, y la guardan esta pantalla y el
                        servidor. */}
                    <p className="text-sm text-muted-foreground mt-1" data-testid="quien-contesta">
                        {!can('chat')
                            ? 'Te contesta el equipo.'
                            : (profile?.entrenador?.nombre
                                ? `Te contesta ${profile.entrenador.nombre}, tu entrenador.`
                                : 'Te contesta el equipo mientras te asignamos entrenador.')}
                    </p>
                </div>
                {sinLeer > 0 && entradaParaLeer && (
                    <button onClick={() => { setLoading(true); setCanal(entradaParaLeer); }}
                        data-testid="leer-respuestas"
                        className="w-full p-4 rounded-xl border border-brand/50 bg-brand/10 flex items-center gap-4 text-left hover:bg-brand/15 transition-colors">
                        <div className="w-11 h-11 bg-brand rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold">
                            {sinLeer}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="font-bold text-foreground text-sm">
                                {sinLeer === 1 ? 'Tienes 1 respuesta sin leer' : `Tienes ${sinLeer} respuestas sin leer`}
                            </p>
                            <p className="text-muted-foreground text-sm">Te hemos contestado. Pulsa para leerlo.</p>
                        </div>
                        <span className="flex items-center gap-1 text-sm font-semibold text-brand flex-shrink-0">
                            Leer <ChevronRight className="w-4 h-4" />
                        </span>
                    </button>
                )}
                {puertas.map(([clave, c]) => (
                    <button key={clave} onClick={() => { setLoading(true); setCanal(clave); }}
                        data-testid={`entrada-${clave}`}
                        className="surface surface-hover group w-full p-4 flex items-center gap-4 text-left">
                        <div className="w-11 h-11 bg-brand/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <c.icono className="w-5 h-5 text-brand" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="font-bold text-foreground text-sm">{c.etiqueta}</p>
                            <p className="text-muted-foreground text-sm">{c.detalle}</p>
                            {/* El horario, debajo (lo dictó Jesús con la puerta). */}
                            {c.horario && (
                                <p className="text-xs text-brand mt-1" data-testid={`horario-${clave}`}>{c.horario}</p>
                            )}
                        </div>
                        <span className="flex items-center gap-1 text-sm font-semibold text-brand flex-shrink-0">
                            Escribir <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                    </button>
                ))}
                {/* El Premium no pregunta por aquí lo suyo: lo suyo va por WhatsApp con su
                    entrenador, como siempre. Es una tarjeta informativa, no una puerta del
                    chat: su botón sale de la app y abre WhatsApp con el número del equipo. */}
                {/* Y SIN NÚMERO NO SE PROMETE (Gonzalo, 4-09, ficha 89; cerrado el 13-09). El
                    botón ya estaba, pero colgaba de que hubiera número y el cartel no: con el
                    ajuste vacío -- que es como está hoy en dev, `app_settings.whatsapp_entrenador`
                    sin poner -- el Premium leía «hablas con tu entrenador por WhatsApp, como
                    siempre» y debajo no había nada que pulsar. Eso es exactamente lo que
                    denuncia la ficha: te manda a WhatsApp y no te lleva. Una puerta sin manilla
                    se queda fuera; en cuanto el equipo ponga el número en Ajustes de la app, la
                    tarjeta vuelve con su botón. */}
                {seguimientoPorWhatsapp && whatsapp && (
                    <div className="surface w-full p-4 flex items-center gap-4" data-testid="entrada-seguimiento">
                        <div className="w-11 h-11 bg-brand/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <MessageCircle className="w-5 h-5 text-brand" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="font-bold text-foreground text-sm">Tu seguimiento</p>
                            <p className="text-muted-foreground text-sm">
                                Para lo tuyo - dieta, entreno, cómo vas - hablas con tu entrenador por WhatsApp, como siempre.
                            </p>
                            {whatsapp && (
                                <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer"
                                    data-testid="escribir-whatsapp"
                                    className="inline-flex items-center gap-1.5 mt-3 px-3 py-2 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand/90 transition-colors">
                                    <MessageCircle className="w-4 h-4" /> Escribir por WhatsApp
                                </a>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ─── La conversación de siempre, etiquetada con su canal ───────────────────
    if (loading) {
        return (
            <div className="h-[calc(100vh-8rem)] md:h-[calc(100vh-2rem)] flex items-center justify-center bg-background">
                <div className="animate-spin w-8 h-8 border-2 border-[#FF671F] border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-8rem)] md:h-[calc(100vh-2rem)] flex flex-col animate-fade-in bg-background">
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center gap-3 bg-card" data-testid="messages-content">
                <button onClick={() => setCanal(null)} data-testid="volver-entradas"
                    className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                    aria-label="Volver a elegir con qué te ayudamos">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                {/* La inicial de su entrenador, no un muñeco de dicebear (punto 4.18). */}
                <Avatar className="border-2 border-[#FF671F]">
                    <AvatarFallback className="bg-[#FF671F] text-white font-bold">
                        {profile?.entrenador?.nombre?.charAt(0)?.toUpperCase() || <User className="w-4 h-4" />}
                    </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                    {/* SU NOMBRE, NO «Tu Entrenador» (punto 4.16). Hablar con la persona que
                        te lleva no puede parecerse a un formulario de soporte. El nombre lo
                        da el servidor en el perfil: el cliente no tiene acceso al listado
                        del equipo. */}
                    <div className="flex items-center gap-2">
                        <h2 className="font-bold text-foreground uppercase tracking-wider" data-testid="messages-heading">
                            {profile?.entrenador?.nombre || '12EN12'}
                        </h2>
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-brand/10 text-brand flex-shrink-0"
                            data-testid="chip-canal">
                            {CANAL[canal].etiqueta}
                        </span>
                    </div>
                    {/* DEBAJO DEL NOMBRE, CUÁNTO SE TARDA EN CONTESTAR.
                        EL PLAZO ES EL DE SU PLAN (tarea 1.5): lo trae el perfil
                        (`tiempo_respuesta`); si su plan no promete plazo, la línea no se
                        pinta -- prometer de más es peor que no decir nada.
                        Y SOLO EN «MI SUSCRIPCIÓN» (doc 21-08): la promesa de tiempo de
                        respuesta se queda donde hay dinero de por medio. En «Algo no
                        funciona» no se promete plazo ninguno. */}
                    {canal === 'suscripcion' && profile?.tiempo_respuesta && (
                        <p className="text-xs text-[#FF671F]">Te respondemos en {profile.tiempo_respuesta}</p>
                    )}
                    {/* Y en «Mi plan», el mismo horario que anuncia la puerta: quien
                        escribe un domingo tiene que saber cuándo se le contesta sin
                        volver atrás a leerlo. */}
                    {CANAL[canal].horario && (
                        <p className="text-xs text-[#FF671F]" data-testid="horario-dentro">{CANAL[canal].horario}</p>
                    )}
                </div>
            </div>

            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                {messages.length > 0 ? (
                    <div className="space-y-4">
                        {messages.map((msg) => {
                            const isOwn = msg.sender_id === user.id;
                            return (
                                <div
                                    key={msg.id}
                                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`max-w-[80%] ${isOwn ? 'order-2' : 'order-1'}`}>
                                        <div
                                            className={`rounded-2xl px-4 py-2 ${
                                                isOwn
                                                    ? 'bg-[#FF671F] text-white rounded-br-sm'
                                                    : 'bg-muted text-foreground rounded-bl-sm'
                                            }`}
                                        >
                                            {msg.content && (
                                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                            )}
                                            {/* La imagen, dentro de la burbuja: con texto va
                                                debajo, y sola es la burbuja entera. */}
                                            {msg.adjunto && (
                                                <div className={msg.content ? 'mt-2' : ''}>
                                                    <ImagenDelMensaje api={api} adjunto={msg.adjunto} propio={isOwn} />
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                                            <span className="text-xs text-foreground/40">
                                                {formatTime(msg.created_at)}
                                            </span>
                                            {isOwn && (
                                                msg.read
                                                    ? <CheckCheck className="w-3 h-3 text-[#FF671F]" />
                                                    : <Check className="w-3 h-3 text-foreground/40" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8">
                        <div className="w-20 h-20 bg-[#FF671F]/10 rounded-full flex items-center justify-center mb-4">
                            <MessageCircle className="w-10 h-10 text-[#FF671F]" />
                        </div>
                        <h3 className="font-bold text-foreground uppercase tracking-wider mb-2">Sin mensajes</h3>
                        {/* Que diga lo mismo que la cabecera (punto 4.16). Sin entrenador
                            asignado, arriba pone «Soporte JG12» y aquí ponía «envía un mensaje
                            a tu entrenador»: la misma pantalla se contradecía a sí misma, y le
                            prometía una persona que ese cliente no tiene todavía.

                            Y NO SE PROMETE UN ENTRENADOR QUE NO VA A LLEGAR (#50 del 15-08).
                            Aquí ponía «en cuanto tengas entrenador asignado, hablarás aquí
                            mismo con él» a un cliente cuyo plan pone «Con entrenador»: el que
                            paga por llevarlo lee que todavía no lo tiene. El acompañamiento
                            del plan y el campo `trainer_id` son dos cosas distintas -- de 247
                            clientes solo 8 lo tienen puesto -- y esta pantalla no es el sitio
                            para explicarlo. Se dice lo único que es verdad siempre: escribe y
                            te contesta quien te lleva. */}
                        <p className="text-sm text-foreground/50">
                            {profile?.entrenador?.nombre
                                ? `Escribe a ${profile.entrenador.nombre} para empezar.`
                                : 'Escríbenos por aquí: te contesta el equipo que lleva tu seguimiento.'}
                        </p>
                    </div>
                )}
            </ScrollArea>

            {/* Input Area */}
            <div className="p-4 border-t border-border bg-card">
                <PreviaDelAdjunto adjunto={adjunto} onQuitar={() => setAdjunto(null)} />
                <form onSubmit={handleSend} className="flex items-center gap-2">
                    <AdjuntarImagen api={api} adjunto={adjunto} onAdjunto={setAdjunto}
                        deshabilitado={sending} />
                    <Input
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Escribe un mensaje..."
                        className="flex-1 bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-brand"
                        disabled={sending}
                        data-testid="message-input"
                    />
                    <Button
                        type="submit"
                        size="icon"
                        disabled={sending || (!newMessage.trim() && !adjunto)}
                        className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white"
                        data-testid="send-message-btn"
                    >
                        <Send className="w-4 h-4" />
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default MessagesPage;
